import type { Pbx } from '../api/types'

/**
 * Day/Night settings processed from PBX data
 */
export interface DayNightSettings {
  /** Default state for Advanced mode (toggle code *3) */
  defaultState: 'open' | 'closed'
  /** States for Basic mode toggle codes (*30 through *39) */
  toggleCodeStates: Record<string, 'open' | 'closed'>
}

/**
 * Process PBX day/night fields into a usable settings object
 * @param pbx - PBX data with day/night fields, or null/undefined
 * @returns Processed day/night settings, or null if PBX is not available
 *
 * Mapping:
 * - day_night → defaultState (Advanced mode, toggle code *3)
 * - day_night_0 → toggleCodeStates['*30']
 * - day_night_1 → toggleCodeStates['*31']
 * - ... (pattern: day_night_N → toggleCodeStates['*3N'])
 *
 * Values: "0" = CLOSED, "1" = OPEN
 */
export function processDayNightSettings(
  pbx: Pbx | null | undefined,
): DayNightSettings | null {
  if (!pbx) {
    return null
  }

  // Map day_night to defaultState
  const defaultState: 'open' | 'closed' =
    pbx.day_night === '1' ? 'open' : 'closed'

  // Map day_night_0 through day_night_9 to toggle code states
  const toggleCodeStates: Record<string, 'open' | 'closed'> = {}
  for (let i = 0; i <= 9; i++) {
    const fieldName = `day_night_${i}` as keyof Pbx
    const fieldValue = pbx[fieldName]
    // Toggle code is *3N where N is the digit (0-9)
    const toggleCode = `*3${i}`
    // Default to 'closed' for invalid values (not "0" or "1")
    toggleCodeStates[toggleCode] = fieldValue === '1' ? 'open' : 'closed'
  }

  return {
    defaultState,
    toggleCodeStates,
  }
}

/**
 * Get the state for a specific toggle code from settings
 * @param settings - Day/night settings (can be null)
 * @param toggleCode - Toggle code (e.g., "*30", "*37", null for Advanced mode)
 * @returns State for the toggle code, or 'closed' if not found or settings unavailable
 */
export function getToggleCodeState(
  settings: DayNightSettings | null,
  toggleCode: string | null,
): 'open' | 'closed' {
  if (!settings) {
    return 'closed'
  }

  if (!toggleCode || toggleCode === '*3') {
    // Advanced mode: use default state
    return settings.defaultState
  }

  // Basic mode: look up toggle code state
  return settings.toggleCodeStates[toggleCode] || 'closed'
}
